import { useState, useEffect, useCallback } from 'react';

/**
 * Доба й горизонт макета «Торговий графік» (/dispatch/schedule-draft).
 *
 * Живе тут, а не в сторінці, бо значення поділяють два місця: тіло сторінки і
 * винесене в топбар керування добою (D+1 / D+2 · дата · ◀ ▶). Той самий прийом,
 * що в useForecastMmsDate — модульне значення + власна подія синхронізації в
 * межах вкладки: браузерна 'storage' спрацьовує лише для ІНШИХ вкладок.
 *
 * Без localStorage: при перезавантаженні доба повертається на D+1, що і є
 * розумним типовим значенням для графіка, який подають на завтра.
 */
const CHANGE_EVENT = 'helios:schedule-draft-change';

export type DraftHorizon = 'd1' | 'd2';

/** Зсув ISO-дати на ±N діб у UTC (без стрибків через переведення годинника). */
export function shiftDraftDay(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

function todayIso(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

/** Горизонт за датою: D+1 / D+2 відносно сьогодні, інакше — нічого не підсвічуємо. */
export function horizonOf(iso: string): DraftHorizon | '' {
  const t = todayIso();
  if (iso === shiftDraftDay(t, 1)) return 'd1';
  if (iso === shiftDraftDay(t, 2)) return 'd2';
  return '';
}

let current = shiftDraftDay(todayIso(), 1);

export function useScheduleDraftUi() {
  const [date, setDateState] = useState(current);

  const setDate = useCallback((d: string) => {
    current = d;
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    setDateState(d);
  }, []);

  /** Перемикач D+1 / D+2 — рахує дату від сьогодні. */
  const setHorizon = useCallback((h: DraftHorizon) => {
    setDate(shiftDraftDay(todayIso(), h === 'd1' ? 1 : 2));
  }, [setDate]);

  useEffect(() => {
    const refresh = () => setDateState(current);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => window.removeEventListener(CHANGE_EVENT, refresh);
  }, []);

  return { date, setDate, setHorizon, horizon: horizonOf(date) };
}
